import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Select } from 'ncoded-component-library';
import ReactCountryFlag from 'react-country-flag';
import { ALL_COUNTRIES } from 'enums';
import type { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import { ExtractPropTypes } from 'types';
import './CountryPicker.styles.scss';

export type Country = {
  code: string;
  call: string;
  name: string;
};

type SelectProps = ExtractPropTypes<typeof Select>;

type CountryPickerProps = {
  defaultCountry?: string;
  countries?: string[];
  className?: string;
  isPartOfPhoneField?: boolean;
  displayValues?: Array<keyof Country>;
  searchValues: Array<keyof Country>;
  disabled?: boolean;
  onChange?: (value: OptionValue) => void;
} & Omit<SelectProps, 'options' | 'multiple' | 'onChange' | 'value'>;

const CountryPicker: React.FC<CountryPickerProps> = (props) => {
  const {
    defaultCountry = 'DE',
    countries,
    className,
    isPartOfPhoneField,
    displayValues,
    searchValues,
    onChange,
    disabled,
    ...rest
  } = props;

  const countryOptions = useMemo(() => {
    const filteredCountries = !countries
      ? ALL_COUNTRIES
      : ALL_COUNTRIES.filter((country) =>
          countries.find((countryCode) => country.code === countryCode),
        );

    return filteredCountries.map((country) => ({
      label: {
        icon: ReactCountryFlag({ countryCode: country.code, svg: true }),
        text: displayValues
          ? displayValues.map((value) => country[value]).join(' ')
          : '',
      },
      ...(searchValues && {
        searchValue: searchValues
          .map((value) => country[value])
          .join(isPartOfPhoneField ? '      ' : ' '),
      }),
      value: country.code,
    }));
  }, [countries, displayValues, isPartOfPhoneField, searchValues]);

  const [value, setValue] = useState<OptionValue>(
    countryOptions.find(({ value }) => value === defaultCountry),
  );

  useEffect(() => {
    setValue(countryOptions.find(({ value }) => value === defaultCountry));
  }, [countryOptions, defaultCountry]);

  const handleOnChange = useCallback(
    (option: OptionValue) => {
      setValue(option);
      onChange && onChange(option);
    },
    [onChange],
  );

  const classes = classNames(
    'fm-country-picker',
    disabled ? 'fm-country-picker--disabled' : '',
    className,
  );

  return (
    <Select
      value={value}
      className={classes}
      {...rest}
      searchable={!!searchValues}
      options={countryOptions}
      onChange={(option: OptionValue) => handleOnChange(option)}
    />
  );
};

export default CountryPicker;
